import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import logo from '../../../icons/logo.png';
import BankVoucherLayout from '../../../base-components/BankVoucherLayout';
import { encodedImagesURI } from '../../../utils/helpers';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const steps = ['Details', 'Preview', 'Confirm'];

function canStaffAccess(role) {
  return ['superadmin', 'admin', 'clubadmin'].includes((role || '').toLowerCase());
}

function DefaultVoucherPreviewBody({
  voucher,
  member,
  companyData,
  member_package_amount,
}) {
  return (
    <>
      <div className="border-b-2 mb-10 flex justify-between items-center print:mx-0">
        <img
          src={companyData?.company_logo ? encodedImagesURI(companyData.company_logo) : logo}
          alt=""
          className="text-center p-2 w-24 md:w-28 lg:w-32 lg:p-4 xl:w-40"
          style={{ objectFit: 'contain', maxHeight: '170px' }}
        />
      </div>

      <div className="flex justify-between items-start mx-[11px] text-[8px] gap-4 print:mx-5 print:my-16 md:mx-4 md:text-sm md:gap-10 lg:text-sm xl:text-md xl:text-xl print:text-lg">
        <div className="w-1/3">
          <p>
            <strong className="mr-2">From:</strong>
            {member?.company_id?.company_name}
          </p>
          <p>
            <strong className="mr-2">Address:</strong>
            {member?.location_id?.address}
          </p>
          <p>
            <strong className="mr-2">Email:</strong>
            {member?.location_id?.email}
          </p>
          <p>
            <strong className="mr-2">Phone No:</strong>
            {member?.location_id?.phone_number}
          </p>
          <p>
            <strong className="mr-2">Package Duration:</strong>
            {member_package_amount?.duration}
          </p>
        </div>
        <div className="w-1/3">
          <p>
            <strong className="mr-2">To:</strong>
            {member?.name}
          </p>
          <p>
            <strong className="mr-2">Address: </strong>
            {member?.address}
          </p>
          <p>
            <strong className="mr-2">Phone No: </strong>
            {member?.phone}
          </p>
          <p>
            <strong className="mr-2">Email: </strong>
            {member?.email}
          </p>
          <p>
            <strong className="mr-2">Package Name:</strong>
            {member_package_amount?.title}
          </p>
          <p>
            <strong className="mr-2">Member ID:</strong>
            {member?.member_id}
          </p>
        </div>
        <div className="w-1/3">
          <p>
            <strong className="mr-2">Invoice Date: </strong>
            {voucher?.voucher_date ? dayjs(voucher.voucher_date).format('MMM DD, YYYY') : ''}
          </p>
          <p>
            <strong className="mr-2">Invoice No:</strong>
            {voucher?.invoice_no}
          </p>
          {voucher?.company_has_bank_integration === true && (
            <p>
              <strong className="mr-2">Bank Voucher Number:</strong>
              {voucher?.transaction_number || 'Assigned at creation'}
            </p>
          )}
          <p>
            <strong className="mr-2">Due Date: </strong>
            {voucher?.invoice?.due_date ? dayjs(voucher.invoice.due_date).format('MMM DD, YYYY') : ''}
          </p>
          <p>
            <strong className="mr-2">Subscription Valid Till: </strong>
            {voucher?.subscription_id?.end_date
              ? dayjs(voucher.subscription_id.end_date).format('MMM DD, YYYY')
              : ''}
          </p>
          <p>
            <strong className="mr-2">Subscription Start: </strong>
            {voucher?.subscription_id?.start_date
              ? dayjs(voucher.subscription_id.start_date).format('MMM DD, YYYY')
              : ''}
          </p>
        </div>
      </div>
      <div className=" mt-10 print:mt-16 print:mb-5 mx-4 print:mx-5 font-bold text-[8px] md:text-sm lg:text-sm xl:text-xl print:text-lg ">
        Charges:
      </div>
      <div className="text-left mx-10 my-5 print:mx-5 ">
        <table className="w-full">
          <tbody className="text-[8px] md:text-sm lg:text-sm xl:text-xl print:text-lg">
            {voucher?.invoice?.membership_fee > 0 && (
              <tr className="border-b-2">
                <td className="py-2">Membership Fee</td>
                <td className="py-2">{voucher?.invoice?.membership_fee}</td>
              </tr>
            )}
            {voucher?.invoice?.registration_fee > 0 && (
              <tr className="border-b-2">
                <td className="py-2">Registration Fee</td>
                <td className="py-2">{voucher?.invoice?.registration_fee}</td>
              </tr>
            )}
            {voucher?.invoice?.trainer_fee > 0 && (
              <tr className="border-b-2">
                <td className="py-2">Trainer Fee ({voucher?.trainer_id?.name})</td>
                <td className="py-2">{voucher?.invoice?.trainer_fee}</td>
              </tr>
            )}
            {voucher?.invoice?.facilities_fee > 0 && (
              <tr className="border-b-2">
                <td className="py-2">Facilities Fee</td>
                <td className="py-2">{voucher?.invoice?.facilities_fee}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between mx-4 print:mx-5 my-8 print:my-10 text-[8px] md:text-sm lg:text-sm xl:text-xl print:text-lg font-bold">
        <p>Total</p>
        <p>{voucher?.invoice?.total_amount}</p>
      </div>
      <div className="flex justify-between mx-4 print:mx-5 my-8 print:my-10 text-[8px] md:text-sm lg:text-sm xl:text-xl print:text-lg font-bold">
        <p>Pending</p>
        <p>{voucher?.invoice?.pending_amount}</p>
      </div>
    </>
  );
}

export default function AdminCreateVoucher() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [activeStep, setActiveStep] = useState(0);
  const [role, setRole] = useState('');
  const [userId, setUserId] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [company, setCompany] = useState(null);
  const [members, setMembers] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [memberId, setMemberId] = useState('');
  const [populatedMember, setPopulatedMember] = useState(null);
  const [voucherKind, setVoucherKind] = useState('membership');
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs().add(1, 'month'));
  const [trainerId, setTrainerId] = useState('');
  const [previewRes, setPreviewRes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [returnSubscriptionId, setReturnSubscriptionId] = useState(null);
  const [lockMemberSelection, setLockMemberSelection] = useState(false);

  useEffect(() => {
    const st = location.state;
    if (st?.returnSubscriptionId) {
      setReturnSubscriptionId(st.returnSubscriptionId);
    }
    setLockMemberSelection(
      Boolean(st?.returnSubscriptionId && st?.preselectedMemberId)
    );
    const fromQuery = searchParams.get('memberId');
    const fromState = st?.preselectedMemberId;
    const mid = fromState || fromQuery;
    if (mid) {
      setMemberId(String(mid));
    }
  }, [location.state, location.key, searchParams]);

  useEffect(() => {
    const sessionStr = sessionStorage.getItem('user-info');
    if (!sessionStr) {
      navigate('/login');
      return;
    }
    const session = JSON.parse(sessionStr);
    const r = session.user.role || '';
    if (!canStaffAccess(r)) {
      navigate('/dashboard');
      return;
    }
    setRole(r);
    setUserId(session.user._id);
    setCompanyId(session.user.company_id);
    setLocationId(session.user.location_id || '');
  }, [navigate]);

  useEffect(() => {
    if (!companyId) return;
    (async () => {
      try {
        const res = await fetch(`${BASE_URL}company/${companyId}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.message);
        setCompany(data);
      } catch (e) {
        setError(e.message || 'Failed to load company');
      } finally {
        setLoading(false);
      }
    })();
  }, [companyId]);

  useEffect(() => {
    if (!companyId || !role) return;
    const rl = (role || '').toLowerCase();
    const endpoint =
      rl === 'clubadmin'
        ? `members/getMembersByLocationID/${locationId}?all=true`
        : `members/getMembersByCompanyID/${companyId}?all=true`;
    if (rl === 'clubadmin' && !locationId) {
      setMembers([]);
      return;
    }
    fetch(BASE_URL + endpoint)
      .then((r) => r.json())
      .then((data) => setMembers(data.members || []))
      .catch(() => setMembers([]));
  }, [companyId, role, locationId]);

  useEffect(() => {
    const loc =
      populatedMember?.location_id?._id ||
      populatedMember?.location_id ||
      locationId;
    if (!loc || voucherKind !== 'trainer') {
      setTrainers([]);
      return;
    }
    fetch(`${BASE_URL}trainers/get-trainers-by-location/${loc}`)
      .then((r) => r.json())
      .then((data) => setTrainers(data.trainers || []))
      .catch(() => setTrainers([]));
  }, [populatedMember, locationId, voucherKind]);

  useEffect(() => {
    if (!memberId) {
      setPopulatedMember(null);
      return;
    }
    fetch(`${BASE_URL}members/get-member/${memberId}`)
      .then((r) => r.json())
      .then((data) => setPopulatedMember(data.member || null))
      .catch(() => setPopulatedMember(null));
  }, [memberId]);

  useEffect(() => {
    if (voucherKind === 'membership' && startDate?.isValid?.()) {
      setEndDate(startDate.add(1, 'month'));
    }
  }, [startDate, voucherKind]);

  const blockedByFlag = company && !company.allow_voucher_creation;

  const activeSubscriptionMembers = useMemo(
    () =>
      (members || []).filter(
        (m) => m.subscription && m.subscription.is_active === true
      ),
    [members]
  );

  const memberAutocompleteOptions = useMemo(() => {
    const base = activeSubscriptionMembers;
    if (
      !lockMemberSelection ||
      !memberId ||
      !populatedMember ||
      String(populatedMember._id) !== String(memberId)
    ) {
      return base;
    }
    if (base.some((m) => String(m._id) === String(memberId))) {
      return base;
    }
    return [
      {
        _id: populatedMember._id,
        name: populatedMember.name,
        member_id: populatedMember.member_id,
        subscription: { is_active: true },
      },
      ...base,
    ];
  }, [
    activeSubscriptionMembers,
    lockMemberSelection,
    memberId,
    populatedMember,
  ]);

  useEffect(() => {
    if (lockMemberSelection) return;
    if (!memberId) return;
    if (members.length === 0) return;
    const ok = activeSubscriptionMembers.some(
      (m) => String(m._id) === String(memberId)
    );
    if (!ok) setMemberId('');
  }, [
    lockMemberSelection,
    memberId,
    members.length,
    activeSubscriptionMembers,
  ]);

  const previewVoucher = useMemo(() => {
    if (!previewRes?.preview || !populatedMember) return null;
    const p = previewRes.preview;
    const voucherDate =
      p.voucherKind === 'membership'
        ? p.subscription?.start_date
          ? dayjs(p.subscription.start_date).toDate()
          : dayjs().toDate()
        : dayjs().toDate();
    const due = dayjs(voucherDate).add(5, 'day').toDate();
    const subStart = p.subscription?.start_date
      ? dayjs(p.subscription.start_date).toDate()
      : null;
    const subEnd = p.subscription?.end_date
      ? dayjs(p.subscription.end_date).toDate()
      : null;

    const invoice = {
      membership_fee: p.fees.membership_fee,
      registration_fee: p.fees.registration_fee,
      trainer_fee: p.fees.trainer_fee,
      facilities_fee: p.fees.facilities_fee,
      due_date: due,
      total_amount: p.fees.total_amount,
      pending_amount: p.fees.pending_amount,
      is_paid: false,
      payment_status: 'Pending',
      discount: 0,
    };

    return {
      invoice_no: 'Preview',
      voucher_type: 'Invoice',
      voucher_date: voucherDate,
      invoice,
      company_has_bank_integration: p.company_has_bank_integration,
      transaction_number: null,
      trainer_id:
        p.voucherKind === 'trainer' && p.trainer
          ? { name: p.trainer.name }
          : populatedMember?.trainer_id,
      subscription_id: subStart || subEnd ? { start_date: subStart, end_date: subEnd } : null,
    };
  }, [previewRes, populatedMember]);

  const memberPackageAmount = useMemo(() => {
    if (!previewRes?.preview?.package) return populatedMember?.package_id || {};
    return {
      title: previewRes.preview.package.title,
      duration: previewRes.preview.package.duration,
    };
  }, [previewRes, populatedMember]);

  const runPreview = async () => {
    setError('');
    setBusy(true);
    try {
      const body = {
        actingUserId: userId,
        companyId,
        memberId,
        voucherKind,
      };
      if (voucherKind === 'membership') {
        body.startDate = startDate.format('YYYY-MM-DD');
        body.endDate = endDate.format('YYYY-MM-DD');
      } else {
        body.trainerId = trainerId;
      }
      const res = await fetch(`${BASE_URL}voucher/admin/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Preview failed');
      setPreviewRes(data);
      setActiveStep(1);
    } catch (e) {
      setError(e.message || 'Preview failed');
    } finally {
      setBusy(false);
    }
  };

  const confirmCreate = async () => {
    setError('');
    setBusy(true);
    try {
      const body = {
        actingUserId: userId,
        companyId,
        memberId,
        voucherKind,
        previewChecksum: previewRes.previewChecksum,
      };
      if (voucherKind === 'membership') {
        body.startDate = startDate.format('YYYY-MM-DD');
        body.endDate = endDate.format('YYYY-MM-DD');
      } else {
        body.trainerId = trainerId;
      }
      const res = await fetch(`${BASE_URL}voucher/admin/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Create failed');
      navigate(`/voucher/${data.voucherId}`);
    } catch (e) {
      setError(e.message || 'Create failed');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={10}>
        <CircularProgress />
      </Box>
    );
  }

  if (blockedByFlag) {
    return (
      <Box sx={{ m: 3 }}>
        <Alert severity="warning">
          Voucher creation is disabled for this business. Enable it under Business settings.
        </Alert>
        <Button sx={{ mt: 2 }} variant="outlined" onClick={() => navigate('/business-settings')}>
          Business settings
        </Button>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ m: 3, maxWidth: 960 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1 }}>
          <Typography variant="h5" fontWeight={700}>
            Create voucher (admin)
          </Typography>
          {returnSubscriptionId && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate(`/vouchers/${returnSubscriptionId}`)}
            >
              Back to voucher list
            </Button>
          )}
        </Box>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {activeStep === 0 && (
          <Card>
            <CardContent>
              <Autocomplete
                fullWidth
                disabled={lockMemberSelection}
                sx={{ mb: 2 }}
                options={memberAutocompleteOptions}
                getOptionLabel={(option) =>
                  `${option.name ?? ''} (${option.member_id ?? ''})`
                }
                value={
                  memberAutocompleteOptions.find(
                    (m) => String(m._id) === String(memberId)
                  ) || null
                }
                onChange={(_, newValue) => {
                  if (lockMemberSelection) return;
                  setMemberId(newValue ? String(newValue._id) : '');
                }}
                isOptionEqualToValue={(a, b) =>
                  Boolean(a && b && String(a._id) === String(b._id))
                }
                disableClearable={lockMemberSelection}
                noOptionsText="No members with an active subscription"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Member"
                    placeholder={
                      lockMemberSelection
                        ? ''
                        : 'Search by name or member ID'
                    }
                    helperText={
                      lockMemberSelection
                        ? 'Member is fixed when opened from the voucher list.'
                        : undefined
                    }
                  />
                )}
              />

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Voucher type</InputLabel>
                <Select
                  label="Voucher type"
                  value={voucherKind}
                  onChange={(e) => setVoucherKind(e.target.value)}
                >
                  <MenuItem value="membership">Membership</MenuItem>
                  <MenuItem value="trainer">Trainer</MenuItem>
                </Select>
              </FormControl>

              {voucherKind === 'membership' && (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  <DatePicker
                    label="Start date"
                    value={startDate}
                    onChange={(v) => v && setStartDate(v)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                  <DatePicker
                    label="End date"
                    value={endDate}
                    onChange={(v) => v && setEndDate(v)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Box>
              )}

              {voucherKind === 'trainer' && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Trainer</InputLabel>
                  <Select
                    label="Trainer"
                    value={trainerId}
                    onChange={(e) => setTrainerId(e.target.value)}
                  >
                    {trainers.map((t) => (
                      <MenuItem key={t._id} value={t._id}>
                        {t.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <Button
                variant="contained"
                disabled={
                  busy ||
                  !memberId ||
                  (voucherKind === 'trainer' && !trainerId)
                }
                onClick={runPreview}
              >
                {busy ? <CircularProgress size={22} /> : 'Preview'}
              </Button>
            </CardContent>
          </Card>
        )}

        {activeStep === 1 && previewVoucher && populatedMember && (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Preview
              </Typography>
              <Box sx={{ border: '1px solid #ddd', p: 2, bgcolor: '#fff' }}>
                <BankVoucherLayout
                  company_id={companyId}
                  voucherData={{
                    invoice_no: previewVoucher.invoice_no,
                    voucher_no: previewVoucher.invoice_no,
                    voucher_type: previewVoucher.voucher_type,
                    voucher_date: previewVoucher.voucher_date,
                    due_date: previewVoucher.invoice?.due_date,
                    registration_fee: previewVoucher.invoice?.registration_fee,
                    membership_fee: previewVoucher.invoice?.membership_fee,
                    trainer_fee: previewVoucher.invoice?.trainer_fee,
                    facilities_fee: previewVoucher.invoice?.facilities_fee,
                    discount: previewVoucher.invoice?.discount,
                    package_name: memberPackageAmount?.title,
                    is_paid: false,
                    payment_status: previewVoucher.invoice?.payment_status,
                    invoice: previewVoucher.invoice,
                    total_amount: previewVoucher.invoice?.total_amount,
                    pending_amount: previewVoucher.invoice?.pending_amount,
                  }}
                  companyData={company}
                  memberData={{
                    member_id: populatedMember.member_id,
                    name: populatedMember.name,
                    phone: populatedMember.phone,
                    package_name: memberPackageAmount?.title,
                  }}
                >
                  <DefaultVoucherPreviewBody
                    voucher={previewVoucher}
                    member={populatedMember}
                    companyData={company}
                    member_package_amount={memberPackageAmount}
                  />
                </BankVoucherLayout>
              </Box>
              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button onClick={() => setActiveStep(0)}>Back</Button>
                <Button variant="contained" onClick={() => setActiveStep(2)}>
                  Continue to confirm
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {activeStep === 2 && previewRes && (
          <Card>
            <CardContent>
              <Typography gutterBottom>
                Confirm creation for <strong>{populatedMember?.name}</strong> ({previewRes.preview?.voucherKind})
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Totals: pending {previewRes.preview?.fees?.pending_amount}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button onClick={() => setActiveStep(1)}>Back</Button>
                <Button variant="contained" color="primary" disabled={busy} onClick={confirmCreate}>
                  {busy ? <CircularProgress size={22} /> : 'Create voucher'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>
    </LocalizationProvider>
  );
}
